import React, { useState } from 'react';
import { useUrlLocalizer, useTranslator } from 'tradera/lang/translate';
import ALink from 'tradera/components/alink/alink';
import { trackContainerClick } from '../analytics';
import { GtmService } from 'tradera/services/google-tagmanager-service';
import { Button } from 'tradera/components/atoms/button/button';
import { postFavoriteSeller } from '../../vip/api/vip-api';

import classNames from 'classnames';
import { ItemCardImage } from 'tradera/components/item-card/item-card-parts/item-card-image';

import css from './sellers.module.scss';
import Analytics from 'tradera/legacy/static/packages/analytics';
import type { FavouriteSellerCardDto } from 'tradera/state/services/types/webapi-discover-generated';

type SellerCardProps = {
    seller: FavouriteSellerCardDto;
    isSuggestion: boolean;
};

export const SellerCard = ({
    seller,
    isSuggestion,
    ...props
}: SellerCardProps) => {
    const {
        sellerAlias,
        sellerId,
        sellerProfileUrl,
        matchedItems,
        heroImageUrl,
        items
    } = seller;

    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const CAPPED_NUMBER_OF_ITEMS = 3;
    const cappedItems = items?.slice(0, CAPPED_NUMBER_OF_ITEMS);

    const fallbackImages =
        cappedItems && cappedItems.length < CAPPED_NUMBER_OF_ITEMS
            ? Array.from(
                  { length: CAPPED_NUMBER_OF_ITEMS - cappedItems.length },
                  (_value, index) => <Fallback key={`fallback-${index}`} />
              )
            : [];

    return (
        <div className={classNames(css.wrapper)} {...props}>
            {cappedItems && cappedItems.length > 0 && (
                <div className={classNames(css.imageWrapper)}>
                    {cappedItems.map((item) => (
                        <ItemCardImage
                            key={item.itemId}
                            onClick={() => {
                                trackContainerClick(
                                    'favouriteSeller',
                                    'Favourite Seller Card item'
                                );
                            }}
                            itemUrl={item?.itemUrl ?? ''}
                            imageUrl={item?.imageUrl || undefined}
                        />
                    ))}
                    {fallbackImages}
                </div>
            )}
            <div className={css.aliasWrapper}>
                {!!heroImageUrl && (
                    <ALink
                        href={toLocalizedUrl(sellerProfileUrl)}
                        onClick={() => {
                            trackContainerClick(
                                'favouriteSeller',
                                'Favourite Seller Card image'
                            );

                            if (isSuggestion) {
                                Analytics.trackEvent({
                                    category: 'SellerSuggestion',
                                    action: 'NavigateProfile',
                                    label: `${sellerId}:${sellerAlias}`
                                });
                            }
                        }}>
                        <img
                            loading="lazy"
                            src={`https://img.tradera.net/heroimages/small-square/${heroImageUrl}`}
                            alt={sellerAlias ?? ''}
                        />
                    </ALink>
                )}
                <div className="w-100 d-flex justify-content-between align-items-end text-truncate">
                    <div className="text-truncate">
                        <h5 className="text-truncate mb-0">
                            <ALink
                                className="text-truncate text-styled text-dark"
                                href={toLocalizedUrl(`${sellerProfileUrl}`)}
                                onClick={() => {
                                    trackContainerClick(
                                        'favouriteSeller',
                                        'Favourite Seller Card title'
                                    );

                                    if (isSuggestion) {
                                        Analytics.trackEvent({
                                            category: 'SellerSuggestion',
                                            action: 'NavigateProfile',
                                            label: `${sellerId}:${sellerAlias}`
                                        });
                                    }
                                }}
                                title={sellerAlias ?? ''}
                                useLink>
                                {sellerAlias}
                            </ALink>
                        </h5>
                        <ALink
                            className="text-truncate size-oslo"
                            href={toLocalizedUrl(`${sellerProfileUrl}`)}
                            onClick={() => {
                                trackContainerClick(
                                    'favouriteSeller',
                                    'Favourite Seller Card item count'
                                );

                                if (isSuggestion) {
                                    Analytics.trackEvent({
                                        category: 'SellerSuggestion',
                                        action: 'NavigateProfile',
                                        label: `${sellerId}:${sellerAlias}`
                                    });
                                }
                            }}
                            useLink>
                            {t('startPage_favouriteSellers_sellerCard_count', {
                                count: matchedItems
                            })}
                        </ALink>
                    </div>
                    {isSuggestion && (
                        <SuggestedSellerToggle
                            className="flex-shrink-0"
                            sellerId={sellerId}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const Fallback = () => <div className="bg-gray-100 w-100 h-100" />;

type SuggestedSellerToggleProps = Pick<FavouriteSellerCardDto, 'sellerId'> & {
    className?: string;
};

const SuggestedSellerToggle = ({
    sellerId,
    className = ''
}: SuggestedSellerToggleProps) => {
    const { t } = useTranslator();
    const [isFavoriteSeller, setFavoriteSeller] = useState(false);
    const toggleFavoriteSeller = async (
        sellerId: number | undefined,
        isAlreadySaved: boolean
    ) => {
        const data = await postFavoriteSeller(sellerId, isAlreadySaved);
        if (data && 'status' in data && data?.status === 200) {
            setFavoriteSeller(!isFavoriteSeller);
        }
    };
    return (
        <Button
            nowrap
            theme="link"
            className={className}
            onClick={() => {
                GtmService.trackAction(
                    'SellerSuggestion',
                    `${isFavoriteSeller ? 'Remove' : 'Add'} favourite seller`,
                    `SellerId: ${sellerId}`
                );
                toggleFavoriteSeller(sellerId, isFavoriteSeller);
            }}
            data-test-favorite-seller>
            {isFavoriteSeller ? (
                <div data-test-favorite-seller-added>{t('vip_following')}</div>
            ) : (
                <div data-test-favorite-seller-add>{t('vip_follow')}</div>
            )}
        </Button>
    );
};
